/// <amd-module name="InstantSearch/Shared/Services/InvokedProvidersManager" />
import { format } from "VSS/Core/Util/String";
import { IVssService, Services, VssService } from "VSS/Platform/Context";
import { ISearchInput } from "VSS/Platform/Search";
import * as Resources from "InstantSearch/Shared/Resources";
import { IInstantSearchMetadataService } from "InstantSearch/Shared/Services/InstantSearchMetadataService";
import { TelemetryWriter } from "InstantSearch/Shared/TelemetryWriter";
import { IInvokedProvidersInfo } from "InstantSearch/Shared/Types";

export interface IInvokedProvidersManager extends IVssService {
    getProvidersToInvoke(searchInput: ISearchInput, telemetryWriter: TelemetryWriter): IInvokedProvidersInfo;
    setInvokedProvider(key: string, searchInput: ISearchInput, telemetryWriter: TelemetryWriter): void;
    shouldAllowImmersiveSearch(): boolean;
    isQuickNavigationModeOn(): boolean;
}

/**
 * Service for managing invoked instant search providers.
 */
export class InvokedProvidersManager extends VssService implements IInvokedProvidersManager {
    private _invokedProvidersInfo: IInvokedProvidersInfo;

    /**
     * Provides list of menu-providers and provider's props to invoke based on searchText.
     */
    public getProvidersToInvoke(searchInput: ISearchInput, telemetryWriter: TelemetryWriter): IInvokedProvidersInfo {
        if (
            !this._invokedProvidersInfo ||
            !this._invokedProvidersInfo.invokedProvidersProps ||
            this._invokedProvidersInfo.invokedProvidersProps.key === "/"
        ) {
            const searchText = searchInput.queryText().trim();
            // take first or first two words (for '/work item') to support copy paste scenario
            const keys = searchText.toLowerCase().split(" ");
            const singleWordKey = keys[0];
            const twoWordKey = keys.length > 1 ? format("{0} {1}", keys[0], keys[1]) : "";
            const quickNavigationHelpKey = "/";
            const metadataService = this.pageContext.getService<IInstantSearchMetadataService>("IInstantSearchMetadataService");
            this._invokedProvidersInfo = metadataService.getProvidersToInvoke(singleWordKey);

            if (!this._invokedProvidersInfo.invokedProvidersProps /* No provider invoked for single word key */
                && twoWordKey /* twoWordKey is present */
            ) {
                this._invokedProvidersInfo = metadataService.getProvidersToInvoke(twoWordKey);
            }

            if (!this._invokedProvidersInfo.invokedProvidersProps /* No provider invoked for two word key */
                && singleWordKey.startsWith(quickNavigationHelpKey) /* searchText starts with "/" means quick navigation help mode */
            ) {
                // Update to quick navigation help mode instead of default providers mode
                this._invokedProvidersInfo = metadataService.getProvidersToInvoke(quickNavigationHelpKey);
            }

            this._setSearchPrefix(searchText, searchInput, telemetryWriter);
        }

        return this._invokedProvidersInfo;
    }

    /**
     * Sets currently invoked provider and shows search prefix pill
     * when user selects one from quick navigation help dropdown.
     */
    public setInvokedProvider(providerKey: string, searchInput: ISearchInput, telemetryWriter: TelemetryWriter): void {
        const metadataService = this.pageContext.getService<IInstantSearchMetadataService>("IInstantSearchMetadataService");
        this._invokedProvidersInfo = metadataService.getProvidersToInvoke(providerKey);
        this._setSearchPrefix(providerKey, searchInput, telemetryWriter);
    }

    /**
     * Method to check whether quick navigation mode is on.
     */
    public isQuickNavigationModeOn(): boolean {
        if (this._invokedProvidersInfo && this._invokedProvidersInfo.invokedProvidersProps) {
            return true;
        }

        return false;
    }

    /**
     * Method to check if immersive search is supported for invoked provider.
     */
    public shouldAllowImmersiveSearch(): boolean {
        if (!this._invokedProvidersInfo || !this._invokedProvidersInfo.invokedProvidersProps) {
            return true;
        } else {
            const { props } = this._invokedProvidersInfo.invokedProvidersProps;
            return props ? !!props.allowImmersiveSearch : false;
        }
    }

    private _setSearchPrefix(searchText: string, searchInput: ISearchInput, telemetryWriter: TelemetryWriter): void {
        const { replaceText, setSearchPrefixProps, setPlaceholder } = searchInput;
        const { invokedProvidersProps } = this._invokedProvidersInfo;
        if (invokedProvidersProps
            && invokedProvidersProps.props
            && invokedProvidersProps.key !== "/"
            && setSearchPrefixProps
            && setPlaceholder
        ) {
            /* Show pill only if it is not quick navigation help mode */
            const { key, props } = invokedProvidersProps;
            const searchTerms = searchText.substr(key.length).trim();
            replaceText(searchTerms);
            setSearchPrefixProps({ prefixText: key, iconName: props.iconName });
            setPlaceholder(props.placeholder);
            searchInput.onSearchPrefixRemove = () => {
                this._removeInvokedProvider(searchInput);
            };
            telemetryWriter.publish("QuickNavigationActivated", { "type": key });
        }
    }

    private _removeInvokedProvider(searchInput: ISearchInput): void {
        if (this._invokedProvidersInfo) {
            this._invokedProvidersInfo.invokedProvidersProps = undefined;
            const { setPlaceholder, replaceText } = searchInput;
            replaceText("");
            if (setPlaceholder) {
                setPlaceholder(Resources.QuickNavigationPlaceholder);
            }
        }
    }
}

Services.add("IInvokedProvidersManager", { serviceFactory: InvokedProvidersManager });
